import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Heart, CheckCircle, XCircle, HelpCircle, MapPin, Calendar } from '../icons';
import { getRsvpDetails, submitRsvp } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import mediaUrl from '../utils/mediaUrl';

function RsvpPage() {
  const { token } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rsvpStatus, setRsvpStatus] = useState('');
  const [rsvpNote, setRsvpNote] = useState('');
  const [guestCount, setGuestCount] = useState(1);
  const [mealPreference, setMealPreference] = useState('');
  const [kidsCount, setKidsCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    getRsvpDetails(token)
      .then(res => {
        setInvitation(res.data);
        if (res.data.rsvp_status !== 'pending') {
          setRsvpStatus(res.data.rsvp_status);
          setRsvpNote(res.data.rsvp_note || '');
          setGuestCount(res.data.rsvp_guest_count || 1);
          setMealPreference(res.data.meal_preference || '');
          setKidsCount(res.data.rsvp_kids_count || 0);
        }
      })
      .catch(err => {
        // Store a translation key so the message follows the language toggle
        const status = err.response?.status;
        if (status === 404) {
          setError('rsvpPage.invalidLink');
        } else if (!err.response) {
          setError('rsvpPage.connectionError');
        } else {
          setError('rsvpPage.somethingWrong');
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rsvpStatus) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitRsvp(token, { rsvp_status: rsvpStatus, rsvp_note: rsvpNote, rsvp_guest_count: guestCount, meal_preference: mealPreference, rsvp_kids_count: kidsCount });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.response?.data?.error || t('rsvpPage.submitFailed'));
    }
    setSubmitting(false);
  };

  if (loading) return <div className="loading-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="spinner"></div></div>;

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)', padding: '20px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', textAlign: 'center', maxWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <XCircle size={48} style={{ color: '#EF4444', marginBottom: '16px' }} />
          <h2 style={{ color: '#1f2937' }}>{t('rsvpPage.notFound')}</h2>
          <p style={{ color: '#6b7280' }}>{t(error)}</p>
          {error === 'rsvpPage.invalidLink' && <p style={{ color: '#6b7280', fontSize: '14px' }}>{t('rsvpPage.contactCouple')}</p>}
          <Link to="/" className="btn btn-primary" style={{ marginTop: '12px', display: 'inline-block' }}>{t('rsvpPage.goHome')}</Link>
        </div>
      </div>
    );
  }

  const { partner1_name, partner2_name, wedding_date, venue_location, city, message, recipient_name, cover_photo_url, card_template, card_data, audio_url } = invitation;
  const coupleNames = [partner1_name, partner2_name].filter(Boolean).join(' & ');
  const dateStr = wedding_date ? new Date(wedding_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : t('rsvpPage.dateTBD');
  const venueStr = [venue_location, city].filter(Boolean).join(', ') || t('rsvpPage.venueTBD');
  const alreadyResponded = invitation.rsvp_status !== 'pending';
  const cd = card_data || {};
  const primaryColor = cd.primaryColor || '#be185d';
  // Stored as /uploads/... paths; resolve them so they also load in the app
  const cardPhoto = mediaUrl(cd.photoUrl || cover_photo_url);

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)', padding: '20px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '48px', textAlign: 'center', maxWidth: '500px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {rsvpStatus === 'accepted' ? (
            <>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
              <h2 style={{ color: '#22C55E', marginBottom: '8px' }}>{t('rsvpPage.youComing')}</h2>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: 1.6 }}>
                {t('rsvpPage.excitedCelebrate')}
              </p>
            </>
          ) : rsvpStatus === 'declined' ? (
            <>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>💛</div>
              <h2 style={{ color: '#6b7280', marginBottom: '8px' }}>{t('rsvpPage.wellMissYou')}</h2>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: 1.6 }}>
                {t('rsvpPage.understandWish')}
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🤔</div>
              <h2 style={{ color: '#3B82F6', marginBottom: '8px' }}>{t('rsvpPage.thankYou')}</h2>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: 1.6 }}>
                {t('rsvpPage.appreciateLettingKnow')}
              </p>
            </>
          )}
          {/* The server accepts changes to an answer, so let the guest find that */}
          <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '20px' }}>{t('rsvpPage.canChangeLater')}</p>
          <button type="button" className="btn btn-outline"
            onClick={() => { setInvitation(inv => ({ ...inv, rsvp_status: rsvpStatus })); setSubmitted(false); }}>
            {t('rsvpPage.changeMyResponse')}
          </button>
          <p style={{ color: '#9ca3af', fontSize: '13px', marginTop: '24px' }}>
            {t('rsvpPage.poweredBy')} <a href="https://nepwed.com" style={{ color: '#be185d', textDecoration: 'none', fontWeight: 600 }}>NepWed.com</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)', padding: '20px' }}>
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>
        {/* Invitation Card */}
        <div style={{ background: '#fff', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
          {/* Header */}
          <div style={{
            background: cardPhoto
              ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${cardPhoto}) center/cover`
              : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 50%, ${primaryColor}bb 100%)`,
            padding: '48px 30px', textAlign: 'center',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '8px' }}>💍</div>
            <p style={{ color: '#fce7f3', fontSize: '13px', letterSpacing: '3px', textTransform: 'uppercase', margin: '0 0 8px' }}>{cd.subheading || t('rsvpPage.youreInvited')}</p>
            <p style={{ color: '#fce7f3', fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 4px' }}>{t('rsvpPage.theWeddingOf')}</p>
            <h1 style={{ color: '#fff', fontSize: '32px', fontWeight: 600, margin: '12px 0 0', lineHeight: 1.3, fontFamily: cd.fontFamily || 'inherit' }}>{cd.heading || coupleNames}</h1>
          </div>

          {/* Details */}
          <div style={{ padding: '32px 30px' }}>
            <p style={{ color: '#374151', fontSize: '16px', lineHeight: 1.6, textAlign: 'center', marginBottom: '24px' }}>
              {t('rsvpPage.dearGuest')} <strong>{recipient_name}</strong>, {t('rsvpPage.honoredMessage')}
            </p>

            {message && (
              <div style={{ background: '#fdf2f8', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', borderLeft: `3px solid ${primaryColor}` }}>
                <p style={{ color: '#374151', fontSize: '14px', lineHeight: 1.6, fontStyle: 'italic', margin: 0 }}>{message}</p>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: '#f9fafb', borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <Calendar size={20} style={{ color: '#be185d', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('rsvpPage.when')}</div>
                  <div style={{ fontSize: '15px', fontWeight: 500, color: '#1f2937' }}>{dateStr}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <MapPin size={20} style={{ color: '#be185d', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('rsvpPage.where')}</div>
                  <div style={{ fontSize: '15px', fontWeight: 500, color: '#1f2937' }}>{venueStr}</div>
                </div>
              </div>
            </div>

            {/* Audio Message Player */}
            {audio_url && (
              <div style={{ marginTop: '20px', background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)', borderRadius: '12px', padding: '16px 20px', border: `1px solid ${primaryColor}40` }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '10px' }}>
                  💌 {t('rsvpPage.personalMessageFrom')} {[partner1_name, partner2_name].filter(Boolean).join(' & ')}
                </div>
                <audio
                  controls
                  src={mediaUrl(audio_url)}
                  style={{ width: '100%', height: '40px', display: 'block' }}
                />
              </div>
            )}
          </div>
        </div>

        {/* RSVP Form */}
        <div style={{ background: '#fff', borderRadius: '20px', padding: '32px 30px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          <h2 style={{ textAlign: 'center', color: '#1f2937', margin: '0 0 8px', fontSize: '22px' }}>
            <Heart size={20} style={{ color: '#be185d', verticalAlign: 'middle', marginRight: '8px' }} />
            {alreadyResponded ? t('rsvpPage.updateRsvp') : t('rsvpPage.willYouJoin')}
          </h2>
          <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
            {alreadyResponded ? t('rsvpPage.changeResponse') : t('rsvpPage.selectOption')}
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '24px' }}>
              {[
                { value: 'accepted', label: t('rsvpPage.joyfullyAccept'), icon: <CheckCircle size={20} />, bg: '#DCFCE7', border: '#22C55E', color: '#15803d' },
                { value: 'declined', label: t('rsvpPage.regretfullyDecline'), icon: <XCircle size={20} />, bg: '#FEF2F2', border: '#EF4444', color: '#b91c1c' },
                { value: 'maybe', label: t('rsvpPage.maybe'), icon: <HelpCircle size={20} />, bg: '#EFF6FF', border: '#3B82F6', color: '#1d4ed8' },
              ].map(opt => (
                <button key={opt.value} type="button" onClick={() => setRsvpStatus(opt.value)}
                  style={{
                    padding: '14px 24px', borderRadius: '12px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s',
                    background: rsvpStatus === opt.value ? opt.bg : '#fff',
                    border: `2px solid ${rsvpStatus === opt.value ? opt.border : '#e5e7eb'}`,
                    color: rsvpStatus === opt.value ? opt.color : '#6b7280',
                    transform: rsvpStatus === opt.value ? 'scale(1.05)' : 'scale(1)',
                  }}>
                  {opt.icon} {opt.label}
                </button>
              ))}
            </div>

            {rsvpStatus === 'accepted' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  {t('rsvpPage.adultsAttending')}
                </label>
                <select value={guestCount} onChange={e => setGuestCount(parseInt(e.target.value))}
                  style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', width: '100%' }}>
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}

            {rsvpStatus === 'accepted' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  {t('rsvpPage.mealPreference')}
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {[
                    { value: 'veg', label: `🥗 ${t('rsvpPage.vegetarian')}`, bg: '#F0FDF4', border: '#22C55E', color: '#15803d' },
                    { value: 'non_veg', label: `🍗 ${t('rsvpPage.nonVegetarian')}`, bg: '#FFF7ED', border: '#F97316', color: '#c2410c' },
                  ].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setMealPreference(mealPreference === opt.value ? '' : opt.value)}
                      style={{
                        flex: 1, padding: '12px 10px', borderRadius: '10px', fontSize: '13px', fontWeight: 600,
                        cursor: 'pointer', border: `2px solid ${mealPreference === opt.value ? opt.border : '#e5e7eb'}`,
                        background: mealPreference === opt.value ? opt.bg : '#fff',
                        color: mealPreference === opt.value ? opt.color : '#6b7280',
                        transition: 'all 0.2s',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {rsvpStatus === 'accepted' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  {t('rsvpPage.kidsAttending')}
                </label>
                <select value={kidsCount} onChange={e => setKidsCount(parseInt(e.target.value))}
                  style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', width: '100%' }}>
                  {[0, 1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n === 0 ? t('rsvpPage.noKids') : n}</option>)}
                </select>
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                {t('rsvpPage.leaveNote')}
              </label>
              <textarea value={rsvpNote} onChange={e => setRsvpNote(e.target.value)}
                placeholder={t('rsvpPage.notePlaceholder')}
                style={{ width: '100%', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', minHeight: '80px', resize: 'vertical' }} />
            </div>

            <button type="submit" disabled={!rsvpStatus || submitting}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px', fontSize: '16px', fontWeight: 600,
                cursor: rsvpStatus ? 'pointer' : 'not-allowed', border: 'none',
                background: rsvpStatus ? 'linear-gradient(135deg, #be185d, #9d174d)' : '#e5e7eb',
                color: rsvpStatus ? '#fff' : '#9ca3af',
              }}>
              {submitting ? t('rsvpPage.submitting') : t('rsvpPage.submitRsvp')}
            </button>
            {submitError && (
              <p style={{ color: '#EF4444', fontSize: '14px', textAlign: 'center', marginTop: '12px', background: '#FEF2F2', padding: '10px 14px', borderRadius: '8px' }}>{submitError}</p>
            )}
          </form>
        </div>

        <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '12px', marginTop: '24px' }}>
          {t('rsvpPage.poweredBy')} <a href="https://nepwed.com" style={{ color: '#be185d', textDecoration: 'none', fontWeight: 600 }}>NepWed.com</a>
        </p>
      </div>
    </div>
  );
}

export default RsvpPage;
